import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { brl } from "../lib";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIPO_LABEL = { peca: "Peça", servico: "Serviço", material: "Material" };

/**
 * Painel de Brindes — itens dados de graça ao cliente. Custo sempre R$ 0,00 no
 * contratado; `valor_referencia` é interno pra controle. NÃO soma ao Valor Total
 * nem afeta Saldo a Receber.
 * Cliente/arquiteto: read-only.
 */
export default function BrindesPanel({ obraId, canFin, api }) {
  const [data, setData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({
    tipo: "peca", descricao: "", medidas: "", material: "", area_aplicacao: "", valor_referencia: "",
  });

  const token = localStorage.getItem("cps_token");
  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/obras/${obraId}/brindes`, { headers: { Authorization: `Bearer ${token}` } });
      setData(r.data);
    } catch { /* silencioso */ }
  }, [obraId, token]);
  useEffect(() => { load(); }, [load]);

  const adicionar = async () => {
    setErr("");
    const descricao = form.descricao.trim();
    if (!descricao) return setErr("Descrição obrigatória");
    const vr = form.valor_referencia ? parseFloat(String(form.valor_referencia).replace(",", ".")) : 0;
    if (isNaN(vr) || vr < 0) return setErr("Valor de referência inválido");
    setSaving(true);
    try {
      await api("post", `/obras/${obraId}/brindes`, {
        tipo: form.tipo,
        descricao,
        medidas: form.medidas.trim() || undefined,
        material: form.material.trim() || undefined,
        area_aplicacao: form.area_aplicacao.trim() || undefined,
        valor_referencia: vr,
      });
      setForm({ tipo: "peca", descricao: "", medidas: "", material: "", area_aplicacao: "", valor_referencia: "" });
      setShowForm(false);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  const remover = async (bid) => {
    if (!window.confirm("Remover este brinde do histórico?")) return;
    await api("delete", `/obras/${obraId}/brindes/${bid}`);
    await load();
  };

  if (!data) return null;
  const { brindes, qtd, total_referencia } = data;
  const isEmpty = qtd === 0;

  return (
    <div className="panel" style={{ marginBottom: 24 }} data-testid="brindes-panel">
      <div className="panel-head">
        <div>
          <div className="panel-eyebrow">Cortesias</div>
          <h3 className="panel-title">Brindes <em>ao cliente</em></h3>
        </div>
        {canFin && (
          <button className="btn btn-sm" onClick={() => { setShowForm(!showForm); setErr(""); }} data-testid="brinde-add-btn">
            {showForm ? "Cancelar" : "+ Brinde"}
          </button>
        )}
      </div>

      {showForm && canFin && (
        <div style={{ padding: 14, background: "rgba(184,147,90,0.06)", border: "1px solid rgba(184,147,90,0.25)", marginBottom: 14 }} data-testid="brinde-form">
          <div style={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: 10, marginBottom: 10 }}>
            <select className="input" value={form.tipo}
              onChange={e => setForm({ ...form, tipo: e.target.value })}
              data-testid="brinde-tipo">
              <option value="peca">Peça</option>
              <option value="servico">Serviço</option>
              <option value="material">Material</option>
            </select>
            <input type="text" className="input"
              placeholder="Descrição do brinde (ex: Bancada de mármore branco para lavabo)"
              value={form.descricao}
              onChange={e => setForm({ ...form, descricao: e.target.value })}
              data-testid="brinde-descricao" autoFocus />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
            <input type="text" className="input" placeholder="Medidas (ex: 1,67×0,30 m)"
              value={form.medidas} onChange={e => setForm({ ...form, medidas: e.target.value })}
              data-testid="brinde-medidas" />
            <input type="text" className="input" placeholder="Material (ex: Mármore Carrara)"
              value={form.material} onChange={e => setForm({ ...form, material: e.target.value })}
              data-testid="brinde-material" />
            <input type="text" className="input" placeholder="Área (ex: Banheiro social)"
              value={form.area_aplicacao} onChange={e => setForm({ ...form, area_aplicacao: e.target.value })}
              data-testid="brinde-area" />
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <span style={{ fontSize: 11, color: "var(--ink-3)", letterSpacing: ".1em", textTransform: "uppercase" }}>Valor referência (interno):</span>
              <input type="text" className="input" placeholder="R$ 0,00" inputMode="decimal"
                value={form.valor_referencia} onChange={e => setForm({ ...form, valor_referencia: e.target.value })}
                data-testid="brinde-valor" style={{ width: 140 }} />
            </div>
            <button className="btn btn-sm" onClick={adicionar} disabled={saving} data-testid="brinde-confirm">
              {saving ? "Salvando…" : "Salvar brinde"}
            </button>
          </div>
          {err && <div style={{ marginTop: 8, color: "#e35d5d", fontSize: 12 }} data-testid="brinde-err">⚠ {err}</div>}
          <div style={{ marginTop: 10, fontSize: 11, color: "var(--ink-3)", fontStyle: "italic" }}>
            O valor de referência é apenas para controle interno — NÃO soma ao Valor Total nem afeta o Saldo.
          </div>
        </div>
      )}

      {isEmpty ? (
        <div style={{ color: "var(--ink-3)", fontSize: 13, padding: "16px 0", fontStyle: "italic" }}>
          Nenhum brinde registrado.
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "1fr auto", rowGap: 10, columnGap: 16, fontSize: 14, alignItems: "center" }}>
          {brindes.map((b) => (
            <React.Fragment key={b.id}>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }} data-testid={`brinde-row-${b.id}`}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <span style={{
                    fontSize: 10, letterSpacing: ".15em", textTransform: "uppercase",
                    color: "#b8935a", border: "1px solid #b8935a", padding: "1px 8px", borderRadius: 2,
                    background: "rgba(184,147,90,0.08)",
                  }} data-testid={`brinde-badge-${b.id}`}>Brinde</span>
                  <span style={{ fontSize: 10, letterSpacing: ".1em", textTransform: "uppercase", color: "var(--ink-3)" }}>
                    {TIPO_LABEL[b.tipo] || b.tipo}
                  </span>
                  <span style={{ color: "var(--ink)", fontWeight: 500 }}>{b.descricao}</span>
                </div>
                <div style={{ display: "flex", gap: 12, fontSize: 12, color: "var(--ink-3)", flexWrap: "wrap" }}>
                  {b.material && <span>{b.material}</span>}
                  {b.medidas && <span>{b.medidas}</span>}
                  {b.area_aplicacao && <span>Local: {b.area_aplicacao}</span>}
                  {b.valor_referencia > 0 && <span style={{ fontStyle: "italic" }}>ref. {brl(b.valor_referencia)}</span>}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontVariantNumeric: "tabular-nums", color: "#5fa86c", fontWeight: 600 }} data-testid={`brinde-valor-${b.id}`}>
                  R$ 0,00
                </span>
                {canFin && (
                  <button className="btn btn-sm btn-ghost" onClick={() => remover(b.id)}
                    data-testid={`brinde-del-${b.id}`} title="Remover" style={{ color: "#c47a7a" }}>×</button>
                )}
              </div>
            </React.Fragment>
          ))}
          {total_referencia > 0 && (
            <>
              <div style={{ gridColumn: "1/-1", borderTop: "1px dashed rgba(184,147,90,0.3)", marginTop: 6 }} />
              <div style={{ fontSize: 11, color: "var(--ink-3)", fontStyle: "italic" }}>
                Valor de referência acumulado (interno, não soma ao total)
              </div>
              <div style={{ fontVariantNumeric: "tabular-nums", color: "var(--ink-3)", fontSize: 12, fontStyle: "italic" }} data-testid="brinde-total-ref">
                {brl(total_referencia)}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
